import React, { ReactElement } from 'react';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import PeopleIcon from '@mui/icons-material/People';
import TimerIcon from '@mui/icons-material/Timer';
import ListIcon from '@mui/icons-material/List';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import RestoreIcon from '@mui/icons-material/Restore';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ScoreIcon from '@mui/icons-material/Score';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SettingsIcon from '@mui/icons-material/Settings';
import EuroIcon from '@mui/icons-material/Euro';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PostAddIcon from '@mui/icons-material/PostAdd';
import HomeIcon from '@mui/icons-material/Home';
import SearchIcon from '@mui/icons-material/Search';
import SubjectIcon from '@mui/icons-material/Subject';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VideocamIcon from '@mui/icons-material/Videocam';
import StarIcon from '@mui/icons-material/Star';
import BarChartIcon from '@mui/icons-material/BarChart';
import SvgIcon from '@mui/material/SvgIcon';

import FAQIcon from 'assets/icons/faq.svg';
import { licencesTypes } from 'shared/constants/licencesTypes';
import { permissionsTypes } from 'shared/constants/permissionsTypes';
import { allStandardRolesTypes, organisationAdmin } from 'shared/constants/rolesTypes';
import { shopRoles } from 'modules/shop/shared/constants/shopRoles';
import { EnvironmentTypes } from './environmentTypes';

export type SubnavigationItem = {
  to?: string;
  label: string;
  icon: ReactElement;
  role?: string[] | string;
  shopPermissions?: string[];
  permission?: string;
  external?: boolean;
  licence?: string;
  subnavigation?: SubnavigationItem[];
  hidden?: boolean;
  environment?: EnvironmentTypes;
  component?: ReactElement;
};

export type MenuItems = {
  label: string;
  icon: ReactElement;
  licence: string;
  subnavigation: SubnavigationItem[];
  shopPermissions?: string[];
};

export const creditAnalysis: MenuItems = {
  label: 'Finance intelligence',
  icon: <CreditCardIcon />,
  licence: licencesTypes.INVESTORHUB_MODULE,
  subnavigation: [
    {
      to: `${window.location.origin}/page-one`,
      label: 'Overview',
      icon: <ViewCompactIcon />,
      role: allStandardRolesTypes,
    },
    {
      to: '/issuer-dashboard',
      label: 'Issuer Dashboard',
      icon: <InsertChartIcon />,
      shopPermissions: [shopRoles.ISSUER_DASHBOARD],
    },
    {
      to: '/portfolios',
      label: 'Portfolios',
      icon: <FolderSpecialIcon />,
      permission: permissionsTypes.CA_VIEW_DOSSIER,
    },
    {
      to: '/events',
      label: 'Events',
      icon: <EventAvailableIcon />,
      role: allStandardRolesTypes,
    },
    {
      to: '/subscriptions',
      label: 'Subscriptions',
      icon: <EuroIcon />,
      role: allStandardRolesTypes,
    },
  ],
};

export const riskSolutions: MenuItems = {
  label: 'Risk solutions',
  icon: <TrendingUpIcon />,
  licence: licencesTypes.RISKANALYSIS_MODULE,
  subnavigation: [
    {
      to: '/rs/#quick-score',
      label: 'Quick score',
      icon: <ScoreIcon />,
      external: true,
    },
    {
      to: '/rs/#legal-entity-list',
      label: 'Legal entities',
      icon: <LocationCityIcon />,
      external: true,
    },
    {
      to: '/rs/#assessments-list',
      label: 'Assessments',
      icon: <AssessmentIcon />,
      external: true,
    },
    {
      to: '/rs/#settings',
      label: 'Settings',
      icon: <SettingsIcon />,
      external: true,
    },
  ],
};

export const analyticalOperations: MenuItems = {
  label: 'Analytical operations',
  icon: <TimerIcon />,
  licence: licencesTypes.ANALYTICALOPERATIONS_MODULE,
  subnavigation: [
    {
      to: '/ao/#internal-orders',
      label: 'Internal orders',
      icon: <ListIcon />,
      external: true,
    },
    {
      to: '/ao/#roles-management',
      label: 'Roles management',
      icon: <SupervisorAccountIcon />,
      permission: permissionsTypes.A_ANOP_VIEW_ROLES_MANAGEMENT,
      external: true,
    },
  ],
};

export const generalServices: MenuItems = {
  label: 'General services',
  icon: <AccountTreeIcon />,
  licence: licencesTypes.ADMIN_MODULE,
  subnavigation: [
    {
      to: '/admin/#organisations',
      label: 'Dashboard',
      icon: <ViewCompactIcon />,
      permission: permissionsTypes.SSO_VIEW_ORGANISATIONS_LIST,
      external: true,
    },
    {
      to: '/admin/#home',
      label: 'Dashboard',
      icon: <ViewCompactIcon />,
      role: organisationAdmin,
      external: true,
    },
    {
      to: '/admin/#orders',
      label: 'Request history',
      icon: <RestoreIcon />,
      permission: permissionsTypes.CA_SEARCH_ORDER_ITEMS,
      external: true,
    },
    {
      to: '/admin/#leaderboard',
      label: 'Leaderboard',
      icon: <DeveloperBoardIcon />,
      permission: permissionsTypes.CA_VIEW_LEADERBOARD,
      external: true,
    },
    {
      to: '/admin/#master-password',
      label: 'Master password',
      icon: <VpnKeyIcon />,
      permission: permissionsTypes.SSO_MASTER_PASSWORD,
      external: true,
    },
  ],
};

export const subscriptionsServices: MenuItems = {
  label: 'Subscriptions panel',
  icon: <EuroIcon />,
  licence: licencesTypes.INVESTORHUB_MODULE,
  shopPermissions: [shopRoles.SHOP_ADMIN],
  subnavigation: [
    {
      to: '/subscriptions/panel',
      label: 'View products',
      icon: <ViewCompactIcon />,
      shopPermissions: [shopRoles.SHOP_ADMIN],
    },
    {
      to: '/subscriptions/panel/create',
      label: 'Create product',
      icon: <PostAddIcon />,
      shopPermissions: [shopRoles.SHOP_ADMIN],
    },
  ],
};

export const eventsServices: MenuItems = {
  label: 'Events panel',
  icon: <EventAvailableIcon />,
  licence: licencesTypes.INVESTORHUB_MODULE,
  shopPermissions: [shopRoles.SHOP_ADMIN],
  subnavigation: [
    {
      to: '/event-panel/view',
      label: 'View events',
      icon: <ViewCompactIcon />,
      shopPermissions: [shopRoles.SHOP_ADMIN],
    },
    {
      to: '/event-panel/create',
      label: 'Create event',
      icon: <PostAddIcon />,
      shopPermissions: [shopRoles.SHOP_ADMIN],
    },
  ],
};

export const scopeExplorer: MenuItems = {
  label: 'ScopeExplorer',
  icon: <HomeIcon />,
  licence: licencesTypes.SCOPEEXPLORER_MODULE,
  subnavigation: [
    {
      to: '/scopex/en/',
      label: 'Search',
      icon: <SearchIcon />,
      external: true,
    },
    {
      to: '/scopex/en/funds-in-focus',
      label: 'Funds in focus',
      icon: <SubjectIcon />,
      external: true,
    },
    {
      label: 'Services',
      icon: <KeyboardArrowDownIcon />,
      licence: licencesTypes.SCOPEEXPLORER_MODULE,
      subnavigation: [
        {
          to: '/scopex/en/conferences',
          label: 'Conferences',
          icon: <VideocamIcon />,
          external: true,
        },
        {
          to: '/scopex/en/aif-page',
          label: 'OIF Studie 2020',
          icon: <StarIcon />,
          external: true,
        },
      ],
    },
    {
      label: 'Publications',
      icon: <KeyboardArrowDownIcon />,
      licence: licencesTypes.SCOPEEXPLORER_MODULE,
      subnavigation: [
        {
          to: '/scopex/en/news',
          label: 'News',
          icon: <SubjectIcon />,
          external: true,
        },
        {
          to: '/scopex/en/reports',
          label: 'Reports',
          icon: <SubjectIcon />,
          external: true,
        },
        {
          to: '/scopex/en/rating-in-focus',
          label: 'Team Ratings',
          icon: <BarChartIcon />,
          external: true,
        },
      ],
    },
    {
      to: '/scopex/en/asset-managers',
      label: 'Asset Manager',
      icon: <SubjectIcon />,
      external: true,
    },
    {
      to: '/scopex/en/premium',
      label: 'Premium',
      icon: <SubjectIcon />,
      external: true,
    },
  ],
};

const restrictedSubscriptionRatingsFeature =
  process.env.REACT_APP_ENV !== EnvironmentTypes.PRODUCTION;

export const admin: MenuItems = {
  label: 'Admin',
  icon: <SupervisorAccountIcon />, // there will be different icon
  licence: licencesTypes.ADMIN_MODULE,
  subnavigation: [
    {
      to: '/admin-panel/rsr',
      label: 'Restricted Subscription Ratings',
      icon: <ViewCompactIcon />,
      // permission: permissionsTypes.CA_RSR_CONFIG,
      hidden: !restrictedSubscriptionRatingsFeature,
    },
    {
      to: '/admin-panel/psr',
      label: 'Private Subscription Ratings',
      icon: <ViewCompactIcon />,
      // permission: permissionsTypes.CA_RSR_CONFIG,
      hidden: !restrictedSubscriptionRatingsFeature,
    },
    {
      to: '/admin-panel/on-demand',
      label: 'On-Demand Subscription Ratings',
      icon: <ViewCompactIcon />,
      // permission: permissionsTypes.CA_RSR_CONFIG,
      hidden: !restrictedSubscriptionRatingsFeature,
    },
  ],
};
